// PRD 10 §4.2 — one content row: EID / thumbnail / title / preview / category
// badge / status·date / home-exposure / delete. Row click opens the detail/edit
// form (Slice 5); the ✕ delete button is Root-only (Slice 7). Both are wired only
// when the matching handler prop is supplied, so this row renders read-only until
// then. The 홈 노출 toggle (SHCT-486) follows the same convention.

import { useTranslation } from 'react-i18next';

import { decodeHtmlEntities } from '@/shared/utils/decode-html-entities';
import { PERM_DENIED_ATTR } from '@/shared/hooks/useWriteGuard';

import { type ContentSummary } from '../types/health-encyclopedia.types';
import { EncyclopediaStatusBadge } from './EncyclopediaStatusBadge';
import { ThumbnailPlaceholder, ThumbnailWithFallback } from './EncyclopediaThumbnail';

// Title is truncated to 50 chars in the list (PRD §4.2); preview arrives
// already server-truncated (~100 chars).
const TITLE_MAX = 50;
const truncate = (value: string, max: number): string =>
  value.length > max ? `${value.slice(0, max)}...` : value;

// SHCT-649 — PRD 10 v2.16 §4.5 makes 16:9 the canonical ratio and says the admin list shows the
// same original scaled down. The cell was 60×40 (3:2), so a correctly-produced 16:9 thumbnail lost
// its left and right edges here while looking right in the patient app. 64×36 is 16:9 at the same
// row height budget.
const THUMBNAIL_BOX = { width: 64, height: 36, borderRadius: 4 } as const;

interface EncyclopediaListRowProps {
  content: ContentSummary;
  canDelete: boolean;
  canWrite: boolean;
  homeExposedCount: number;
  // The cap the SERVER enforces, served in the list response — not a constant in this
  // file. Passing it down keeps the toggle-disable rule and the tooltip in step with the
  // real guard instead of a copy that goes stale when the server's limit changes.
  maxHomeExposed: number;
  isHomeExposePending?: boolean;
  onRowOpen?: (eid: string) => void;
  onDelete?: (content: ContentSummary) => void;
  onHomeExposeChange?: (content: ContentSummary, next: boolean) => void;
}

export function EncyclopediaListRow({
  content,
  canDelete,
  canWrite,
  homeExposedCount,
  maxHomeExposed,
  isHomeExposePending = false,
  onRowOpen,
  onDelete,
  onHomeExposeChange,
}: EncyclopediaListRowProps) {
  const { t } = useTranslation('health-encyclopedia');
  const clickable = Boolean(onRowOpen);

  // PRD §3.9 조건 분기 — three independent reasons the toggle is unavailable, in
  // the order the PRD lists them. `capReached` only ever blocks turning something
  // ON: an already-ON content must stay switchable so a slot can be freed.
  const isPublished = content.status === 'PUBLISHED';
  const capReached = homeExposedCount >= maxHomeExposed && !content.homeExposed;
  const homeExposeDisabled = !canWrite || !isPublished || capReached || isHomeExposePending;

  const homeExposeTooltip = !canWrite
    ? t('list.homeExposeNoPermTooltip')
    : !isPublished
      ? t('list.homeExposeDraftTooltip')
      : capReached
        ? t('list.homeExposeMaxTooltip', { max: maxHomeExposed })
        : t('list.homeExposeToggle', { max: maxHomeExposed });

  return (
    <tr
      onClick={clickable ? () => onRowOpen?.(content.eid) : undefined}
      style={clickable ? { cursor: 'pointer' } : undefined}
    >
      <td>{content.eid}</td>
      {/* SHCT-716 — a bare <img> here had no `onError` and an empty `alt`, so a
          deleted CDN object (this happens for real: deleting content orphans its
          upload) rendered Chrome's broken-image glyph with no text alternative. */}
      <td>
        {content.thumbnailUrl ? (
          <ThumbnailWithFallback
            url={content.thumbnailUrl}
            alt={content.title}
            style={{ ...THUMBNAIL_BOX, objectFit: 'cover' }}
          />
        ) : (
          <ThumbnailPlaceholder style={THUMBNAIL_BOX} />
        )}
      </td>
      {/* SHCT-606 — truncate by pixel width too (not just by character count),
          with the full value on hover, so a long unbroken string can't stretch
          the column. */}
      <td className="cell-ellipsis" title={content.title}>
        <span className="link-text">{truncate(content.title, TITLE_MAX)}</span>
      </td>
      <td className="cell-ellipsis" title={content.preview ? decodeHtmlEntities(content.preview) : undefined}>
        <span style={{ color: 'var(--muted-foreground)', fontSize: 12 }}>
          {content.preview ? decodeHtmlEntities(content.preview) : '-'}
        </span>
      </td>
      <td>
        <span className="badge badge-outline">
          {content.categoryName ?? t('list.uncategorized')}
        </span>
      </td>
      <td>
        <EncyclopediaStatusBadge content={content} />
      </td>
      {/* PRD §3.9 / §4.2 — 홈 노출 toggle. Same click-swallowing pattern as the ✕
          cell (SHCT-602): the <td> stops propagation so neither the switch nor a
          disabled switch can fall through to the row's edit-open handler. The
          tooltip sits on the <label>, which stays hoverable even when the inner
          <input> is disabled. */}
      <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
        {onHomeExposeChange ? (
          <label className="ency-home-toggle" title={homeExposeTooltip}>
            <input
              type="checkbox"
              role="switch"
              aria-label={t('list.columns.homeExposed')}
              checked={content.homeExposed}
              disabled={homeExposeDisabled}
              // SHCT-582 — PRD 21 §7.4 P-3. The tooltip (P-2) was already right; this is the
              // marker the L2 guard needs, and only when the PERMISSION is what blocks the
              // toggle — a draft row or a full home slot are ordinary states, not refusals.
              {...(canWrite ? {} : { [PERM_DENIED_ATTR]: 'true' })}
              onChange={(e) => onHomeExposeChange(content, e.target.checked)}
            />
            <span className="ency-home-toggle__track" />
          </label>
        ) : null}
      </td>
      {/* The cell — not just the button — swallows the click, so a disabled ✕
          can never fall through to the row's edit-open handler (SHCT-602). */}
      <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
        {/* PRD §3.8 / §8.3 — delete is Root-only. Non-Root sees the ✕ disabled
            with a tooltip rather than a missing control. The tooltip and the
            not-allowed cursor live on a wrapper <span>: `.btn:disabled` sets
            `pointer-events: none`, so a disabled <button> receives no hover and
            a `title` on the button itself would never surface (SHCT-602). */}
        {onDelete ? (
          <span
            title={canDelete ? t('list.deleteRow') : t('list.deleteNoPermTooltip')}
            style={{ display: 'inline-block', cursor: canDelete ? 'pointer' : 'not-allowed' }}
          >
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              aria-label={t('list.deleteRow')}
              disabled={!canDelete}
              {...(canDelete ? {} : { [PERM_DENIED_ATTR]: 'true' })}
              style={{
                color: 'var(--destructive)',
                padding: '2px 8px',
                opacity: canDelete ? 1 : 0.4,
                cursor: canDelete ? 'pointer' : 'not-allowed',
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (canDelete) onDelete(content);
              }}
            >
              ✕
            </button>
          </span>
        ) : null}
      </td>
    </tr>
  );
}
